/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: GPT-5 Codex
 🏢 Company: Ring2All LLC
 📄 File: api/cdrRecordApi.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    API client functions for Call Detail Records module.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  CdrRecord,
  ListCdrRecordsParams,
  ListCdrRecordsResponse,
  GetCdrRecordResponse,
} from '../types';

const CDR_RECORDS_ENDPOINT = '/api/telephony/call-detail-records';

/**
 * Fetch list of CDR records
 */
export async function fetchCdrRecords(
  params: ListCdrRecordsParams,
  domainId?: number
): Promise<ListCdrRecordsResponse> {
  const queryParams = new URLSearchParams();

  if (params.page != null) queryParams.append('page', String(params.page));
  if (params.limit != null) queryParams.append('limit', String(params.limit));
  if (params.search) queryParams.append('search', params.search);
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
  if (params.filterId != null) queryParams.append('filterId', String(params.filterId));
  if (params.startDate) queryParams.append('startDate', params.startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);
  if (params.callerId) queryParams.append('callerId', params.callerId);
  if (params.destinationNumber) queryParams.append('destinationNumber', params.destinationNumber);
  if (params.direction) queryParams.append('direction', params.direction);
  if (params.hangupCause) queryParams.append('hangupCause', params.hangupCause);
  if (domainId != null) queryParams.append('domainId', String(domainId));

  const url = buildApiUrl(`${CDR_RECORDS_ENDPOINT}?${queryParams.toString()}`);
  const response = await fetch(url, {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch CDR records' }));
    throw new Error(error.message || 'Failed to fetch CDR records');
  }

  return response.json();
}

/**
 * Fetch single CDR record by ID or UUID
 */
export async function fetchCdrRecord(id: string | number): Promise<GetCdrRecordResponse> {
  const url = buildApiUrl(`${CDR_RECORDS_ENDPOINT}/${id}`);
  const response = await fetch(url, {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch CDR record' }));
    throw new Error(error.message || 'Failed to fetch CDR record');
  }

  return response.json();
}

